import React, { useContext, useState } from 'react';
import { FirebaseContext } from '../App';
import { deleteVideo } from '../utils/firestoreService';
import './VideoCard.css';

function VideoCard({ video, onDelete, currentUserXUsername }) {
    // ALL REACT HOOKS MUST BE CALLED AT THE TOP LEVEL, BEFORE ANY CONDITIONAL RETURNS
    const { db } = useContext(FirebaseContext);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isHovered, setIsHovered] = useState(false); // New state for hover

    // Now, perform the early return check AFTER all hooks are called
    if (!video || !video.id) {
        console.warn("VideoCard received an invalid video prop:", video);
        return null; // Don't render anything for invalid video
    }

    // Function to extract video ID from YouTube URL
    const getYouTubeEmbedUrl = (url) => {
        const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|)([\w-]{11})(?:\S+)?/i;
        const match = url.match(youtubeRegex);
        if (match && match[1]) {
            return `https://www.youtube.com/embed/${match[1]}?autoplay=0`;
        }
        return null;
    };

    // Updated function to check if it's a direct video link, now robust to query parameters
    const isDirectVideoLink = (url) => {
        // This regex checks for common video file extensions, followed by an optional
        // query string or hash fragment (e.g., .mp4?tag=14 or .webm#t=10)
        return /\.(mp4|webm|ogg|mov|avi|flv|wmv|mkv)(?:\?|#)\S*$/i.test(url) ||
               /\.(mp4|webm|ogg|mov|avi|flv|wmv|mkv)$/i.test(url); // Also check without query for simplicity
    };

    // Function to check if it's an image link (for placeholder/thumbnail)
    const isImageLink = (url) => {
        // This regex checks for common image file extensions, followed by an optional
        // query string or hash fragment
        return /\.(gif|jpg|jpeg|png|webp)(?:\?|#)\S*$/i.test(url) ||
               /\.(gif|jpg|jpeg|png|webp)$/i.test(url); // Also check without query for simplicity
    };

    // Function to get unavatar.io URL for X username
    const getPfpUrl = (username) => {
        if (!username) return `https://placehold.co/24x24/cccccc/333333?text=PFP`; // Fallback placeholder
        return `https://unavatar.io/x/${username}`;
    };

    // Determine the source for the iframe/video element
    // Ensure video.videoUrl exists before attempting to use it
    const embedSrc = video.videoUrl ? getYouTubeEmbedUrl(video.videoUrl) : null;
    const isVideo = (video.videoUrl && embedSrc) || (video.videoUrl && isDirectVideoLink(video.videoUrl));
    const isImage = video.videoUrl && isImageLink(video.videoUrl);


    // Handle video deletion
    const handleDelete = async () => {
        if (!db) {
            console.error("Firestore DB not available for deletion.");
            return;
        }

        setShowDeleteConfirm(false); // Hide confirmation after action

        try {
            const success = await deleteVideo(db, video.id);
            if (success) {
                onDelete(video.id); // Notify parent component to update list
                console.log("Video deleted successfully from UI.");
            } else {
                console.error("Failed to delete video from Firestore.");
            }
        } catch (error) {
            console.error("Error deleting video:", error);
        }
    };

    // Check if the current user is the uploader
    const canDelete = currentUserXUsername && video.uploaderXUsername &&
                      currentUserXUsername.toLowerCase() === video.uploaderXUsername.toLowerCase();


    return (
        <div
            className="video-card"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="video-thumbnail">
                {isVideo ? (
                    // Use a <video> tag for direct video links, and an <iframe> for YouTube embeds
                    embedSrc ? (
                        <iframe
                            src={embedSrc}
                            title={video.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            loading="lazy"
                        ></iframe>
                    ) : (
                        <video controls src={video.videoUrl} loading="lazy">
                            Your browser does not support the video tag.
                        </video>
                    )
                ) : isImage ? (
                    <img
                        src={video.videoUrl}
                        alt={video.title}
                        onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/480x270/cccccc/333333?text=Image+Load+Error`; }}
                        loading="lazy"
                    />
                ) : (
                    <div className="unsupported-media">
                        <p>Unsupported media type or invalid URL.</p>
                        <p>URL: {video.videoUrl || 'N/A'}</p> {/* Display URL if available, else N/A */}
                    </div>
                )}
                {/* Delete icon button - visible on hover or if confirmation is already showing */}
                {canDelete && (isHovered || showDeleteConfirm) && !showDeleteConfirm && (
                    <button className="delete-icon-button" onClick={() => setShowDeleteConfirm(true)} aria-label="Delete video">
                        🗑️
                    </button>
                )}
            </div>
            <div className="video-info">
                <h3 className="video-title">{video.title || 'No Title'}</h3> {/* Fallback for title */}
                <p className="video-uploader">
                    {/* Moved pfp before "Posted by:" text */}
                    {video.uploaderXUsername && (
                        <img
                            src={getPfpUrl(video.uploaderXUsername)}
                            alt={`${video.uploaderXUsername}'s profile`}
                            className="uploader-pfp"
                            onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/24x24/cccccc/333333?text=PFP`; }}
                        />
                    )}
                    <span>Posted by: {video.uploaderXUsername || 'Anonymous'}</span>
                </p>
                <p className="video-category">Category: <span>{video.category || 'N/A'}</span></p>
                {video.description && <p className="video-description">{video.description}</p>}
                <p className="video-date">
                    Uploaded on: {video.timestamp ? new Date(video.timestamp.toDate()).toLocaleDateString() : 'N/A'}
                </p>

                {/* Only show confirmation box when showDeleteConfirm is true */}
                {canDelete && showDeleteConfirm && (
                    <div className="delete-section">
                        <div className="delete-confirm-box">
                            <p>Are you sure you want to delete this video?</p>
                            <button className="confirm-delete-button" onClick={handleDelete}>Yes, Delete</button>
                            <button className="cancel-delete-button" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default VideoCard;
